import allowed from '@/core/constants/allowed-roles.constant'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { IMAGE_INBOX_ENABLED } from '@/modules/consultant-visit-app/constants/cva-image-inbox.constant'
import { COMPANY_IDENTIFIERS, MENU_OVERVIEW_NAME, MODULE_NAMES, ROUTE_NAMES } from '../constants/sidebar.constants'
import { useMenuAuthorization } from './use-menu-authorization'

function getDashboardName(dashboard) {
  if (typeof dashboard.name === 'object') {
    return dashboard.name.en || Object.values(dashboard.name)[0]
  }
  return dashboard.name
}

function sortChildrenWithSettings(children) {
  const nonModuleSettings = children.filter(
    item => !(item.name && item.name.endsWith('module-settings')),
  )
  const moduleSettings = children.filter(
    item => item.name && item.name.endsWith('module-settings'),
  )
  return [...nonModuleSettings, ...moduleSettings]
}

function createDashboardLink(dashboard) {
  return {
    name: getDashboardName(dashboard),
    route: {
      name: ROUTE_NAMES.DASHBOARD_VIEWER,
      params: {
        dashboardId: dashboard._id,
      },
    },
    type: 'sub',
    isRawName: true,
  }
}

function buildOverviewLink(child, children) {
  return children.some(item => item.name === MENU_OVERVIEW_NAME)
    ? []
    : [{ name: MENU_OVERVIEW_NAME, route: child.route, type: 'sub' }]
}

function addImageInboxLink(child, children) {
  if (child.name !== MODULE_NAMES.CONSULTANT_SITE_VISIT || !IMAGE_INBOX_ENABLED) {
    return children
  }

  const overviewLink = buildOverviewLink(child, children)

  return [
    ...overviewLink,
    ...children,
    {
      name: MODULE_NAMES.CONSULTANT_SITE_VISIT_IMAGE_INBOX,
      route: { name: ROUTE_NAMES.CONSULTANT_VISIT_IMAGE_INBOX },
      type: 'sub',
    },
  ]
}

export function useMenuEnrichment() {
  const authStore = useAuthStore()
  const { isModuleEnabled } = useMenuAuthorization()

  const getModuleDashboards = (moduleIdentifier) => {
    return authStore.companyData?.enabledDashboards?.[moduleIdentifier] || []
  }

  const applyAdecoagroCustomization = (child) => {
    if (child.name === MODULE_NAMES.STOP_SAFETY_OBSERVATION && authStore.companyData?.identifier === COMPANY_IDENTIFIERS.ADECOAGRO) {
      return {
        ...child,
        name: MODULE_NAMES.STOP_SAFETY_OBSERVATION_ADECOAGRO,
      }
    }
    return child
  }

  const addCompanionDashboardsLink = (child, children) => {
    const hasCompanionDashboardsRole = authStore.userRoles?.some(
      role => allowed.consultant_visit.companionDashboards.includes(role),
    )

    if (child.name === MODULE_NAMES.CONSULTANT_SITE_VISIT && isModuleEnabled(child.module) && hasCompanionDashboardsRole) {
      return [
        ...children,
        ...buildOverviewLink(child, children),
        {
          name: 'core.menu.modules.dashboards',
          route: { name: ROUTE_NAMES.CONSULTANT_VISIT_DASHBOARDS },
          type: 'sub',
        },
      ]
    }

    return children
  }

  const enrichChildWithDashboards = (child) => {
    const enrichedChild = applyAdecoagroCustomization(child)

    const moduleDashboards = getModuleDashboards(child.module)
    let combinedChildren = child.children ? [...child.children] : []

    if (moduleDashboards.length > 0) {
      const dashboardLinks = moduleDashboards.map(createDashboardLink)
      combinedChildren = [...combinedChildren, ...dashboardLinks]
    }

    combinedChildren = addImageInboxLink(child, combinedChildren)
    combinedChildren = addCompanionDashboardsLink(child, combinedChildren)

    const sortedChildren = sortChildrenWithSettings(combinedChildren)

    return {
      ...enrichedChild,
      children: sortedChildren,
    }
  }

  const enrichModulesWithDashboards = (modules) => {
    return modules.map(module => ({
      ...module,
      children: module.children?.map(enrichChildWithDashboards) || [],
    }))
  }

  return {
    enrichModulesWithDashboards,
    enrichChildWithDashboards,
  }
}
